import type { AgentApproval } from '@tabbio/types';
import { api } from './api';

export type McpScopeType = 'personal' | 'company' | 'mixed';

export interface McpScopeOption {
  scopeType: McpScopeType;
  companyId: string | null;
  label: string;
  company?: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface McpTokenSummary {
  id: string;
  name?: string | null;
  scopeType: McpScopeType;
  companyId?: string | null;
  includePersonal?: boolean;
  companyIds?: string[];
  companies?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  company?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  tokenPrefix: string;
  tokenFingerprint: string;
  maskedToken: string;
  scopes: string[];
  lastUsedAt?: string | Date | null;
  expiresAt?: string | Date | null;
  revokedAt?: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface McpPendingApproval {
  id: string;
  toolName: string;
  args: Record<string, unknown>;
  status: AgentApproval['status'];
  companyId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface McpAccessState {
  serverUrl: string;
  tokens: McpTokenSummary[];
  scopes: McpScopeOption[];
  pendingApprovals: McpPendingApproval[];
}

export interface McpTokenCreateResult {
  token: McpTokenSummary;
  secret: string;
}

function assertData<T>(
  response: { data?: T | null; error?: { message?: string } | null },
  fallback: string,
) {
  if (response.error || !response.data) {
    throw new Error(response.error?.message || fallback);
  }

  return response.data;
}

export async function fetchMcpAccessState() {
  const response = await api.get<McpAccessState>('/api/integrations/mcp');
  return assertData(response, 'Unable to load MCP access');
}

export async function createMcpToken(params: {
  scopeType?: McpScopeType;
  companyId?: string | null;
  companyIds?: string[] | null;
  includePersonal?: boolean;
}) {
  const response = await api.post<McpTokenCreateResult>(
    '/api/integrations/mcp/tokens',
    params,
  );
  return assertData(response, 'Unable to create MCP token');
}

export async function rotateMcpToken(tokenId: string) {
  const response = await api.post<McpTokenCreateResult>(
    `/api/integrations/mcp/tokens/${tokenId}/rotate`,
    {},
  );
  return assertData(response, 'Unable to rotate MCP token');
}

export async function revokeMcpToken(tokenId: string) {
  const response = await api.post<{ revoked: boolean }>(
    `/api/integrations/mcp/tokens/${tokenId}/revoke`,
    {},
  );
  return assertData(response, 'Unable to revoke MCP token');
}
